// Home (RF-UI-04..06, AC-68, AC-74): describe an idea or attach a PRD, choose the
// destination before sending, and follow the resulting project/tasks. The draft lives in
// localStorage (text + destination + idempotency keys, never credentials or file bytes),
// so a retry after a timeout or a reload never creates a duplicate project or import.
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
// The option lists through projectOptions (a module unit tests can supply, like the New project dialog).
import { AUTONOMY_PRESETS } from "../lib/projectOptions.ts";
import { ASSISTANT_ATTACHMENT_ACCEPT, type AutonomyPreset, type PrdImportResult, type ProjectSummary } from "@maestros/contracts";
import { useI18n } from "../i18n/index.tsx";
import { scopeLabel } from "../lib/format.ts";
import { createProject, importPrd, listProjects } from "../api/endpoints.ts";
import { isApiError } from "../api/client.ts";
import { newId, readLocal, removeLocal, writeLocal } from "../lib/storage.ts";
import { PRD_ACCEPT, PrdFileError, kindOf, readPrdFile, type ReadPrd } from "../lib/prdFile.ts";
import { apiErrorView } from "../lib/apiErrors.ts";
import { startPlanningRun } from "../api/endpoints-wave5.ts";
import { useComposerAttachments } from "../features/composer/attachments.ts";
import { useAsync, useDebouncedCallback } from "../lib/useAsync.ts";
import { AGGREGATE_VIEW } from "../lib/status.ts";
import { useRealtimeEvents } from "../realtime/RealtimeProvider.tsx";
import { Button, IconButton } from "../components/Button.tsx";
import { Banner, EmptyState, ErrorBanner, Skeleton } from "../components/Feedback.tsx";
import { ProgressBar, StatusPill } from "../components/Status.tsx";
import { CreateProjectDialog } from "../features/CreateProjectDialog.tsx";
import { CreateTaskDialog } from "../features/CreateTaskDialog.tsx";
import { ImportProgress, ImportResultSummary, type ImportStage } from "../features/PrdImportDialog.tsx";
import { conversationTitleFrom } from "../features/chat/conversationTitle.ts";
import { ConversationHistory } from "../features/chat/ConversationHistory.tsx";
import { homeViewOf, isTrivialProjectBrief, mayCreateProjectFromHome } from "../features/chat/homeView.ts";
import { getAssistantSession } from "../api/endpoints-assistant.ts";
import { HomeAsk, loadHomeChatOptions } from "../features/chat/HomeAsk.tsx";
import { useSetPageTitle } from "../shell/PageTitle.tsx";
import { Composer } from "../features/composer/Composer.tsx";
import {
  ArrowRightIcon,
  ChevronRightIcon,
  FileTextIcon,
  FolderIcon,
  GaugeIcon,
  GitBranchIcon,
  PaperclipIcon,
  PlusIcon,
  SendIcon,
  ServerIcon,
  WrenchIcon,
} from "../components/icons.tsx";

interface HomeDraft {
  text: string;
  destination: "new" | string;
  projectName: string;
  autonomy: AutonomyPreset;
  projectKey: string;
  prdKey: string;
  /** Set once the project exists, so a retry only repeats the PRD import. */
  createdProjectId: string | null;
}

const DRAFT_KEY = "homeDraft";
/** D36: the brief's character limit shown by the composer counter (a long PRD is better attached as a file). */
const HOME_BRIEF_MAX = 200_000;
/**
 * D36.4: the Build composer accepts a PRD document (Markdown, TXT, PDF, DOCX: the PRD import, as before) and every
 * composer attachment type (screenshots and any image, other documents, data and code files), which travel with the
 * brief to the planner.
 */
const HOME_ACCEPT = `${PRD_ACCEPT},${ASSISTANT_ATTACHMENT_ACCEPT}`;

function freshDraft(prev?: Partial<HomeDraft>): HomeDraft {
  return {
    text: "",
    destination: prev?.destination && prev.destination !== "new" ? prev.destination : "new",
    projectName: "",
    autonomy: prev?.autonomy ?? "observation",
    projectKey: newId(),
    prdKey: newId(),
    createdProjectId: null,
  };
}

function loadDraft(): HomeDraft {
  const stored = readLocal<Partial<HomeDraft> | null>(DRAFT_KEY, null);
  if (!stored || typeof stored !== "object") return freshDraft();
  const base = freshDraft();
  return {
    ...base,
    ...stored,
    autonomy: AUTONOMY_PRESETS.includes(stored.autonomy as AutonomyPreset) ? (stored.autonomy as AutonomyPreset) : base.autonomy,
  };
}

function deriveName(text: string, fileTitle: string | null): string {
  if (fileTitle) return fileTitle.slice(0, 120);
  const firstLine = text.trim().split(/\r?\n/)[0] ?? "";
  const clean = firstLine.replace(/^#+\s*/, "").trim();
  return clean.length > 60 ? `${clean.slice(0, 57).trimEnd()}…` : clean;
}

function ProjectMark({ name }: { name: string }) {
  return (
    <span className="project-mark" aria-hidden="true">
      {name.trim().charAt(0).toUpperCase() || "?"}
    </span>
  );
}

function RecentRow({ project }: { project: ProjectSummary }) {
  const { t, formatRelative } = useI18n();
  const view = AGGREGATE_VIEW[project.aggregate_state];
  const { accepted_required: done, total_required: total, scope_label: scope } = project.progress;
  return (
    <li>
      <Link to={`/projects/${project.id}`} className="recent-item card">
        <ProjectMark name={project.name} />
        <span className="recent-main">
          <span className="recent-name truncate">{project.name}</span>
          <span className="row row--wrap recent-meta">
            <StatusPill view={view} />
            <span>{t("projects.activeAgents", { n: project.active_agents })}</span>
            <span aria-hidden="true">·</span>
            <span>{project.last_activity_at ? t("projects.lastActivity", { when: formatRelative(project.last_activity_at) }) : t("projects.noActivity")}</span>
          </span>
          {project.attention_reason ? <span className="recent-attention">{project.attention_reason}</span> : null}
        </span>
        {total > 0 ? (
          <span className="recent-progress">
            <ProgressBar value={done} max={total} label={t("projects.progressShort", { done, total })} detail={scopeLabel(t, scope)} />
          </span>
        ) : (
          <span className="recent-progress subtle">{t("projects.noRequiredTasks")}</span>
        )}
        <ChevronRightIcon className="recent-chevron" />
      </Link>
    </li>
  );
}

export function HomePage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [draft, setDraft] = useState<HomeDraft>(loadDraft);
  const [file, setFile] = useState<ReadPrd | null>(null);
  const [stage, setStage] = useState<ImportStage>("idle");
  const [fraction, setFraction] = useState(0);
  const [error, setError] = useState<{ message: string; detail: string | null; retryable: boolean; correlation: string | null } | null>(null);
  const [result, setResult] = useState<{ project: { id: string; name: string }; prd: PrdImportResult; attachments: number } | null>(null);
  const [dialog, setDialog] = useState<null | "project" | "import" | "fix">(null);
  const { sessionId } = useParams<{ sessionId: string }>();
  const [observedSessionId, setObservedSessionId] = useState<string | null>(null);
  const summaryId = sessionId ?? observedSessionId;
  const session = useAsync((signal) => summaryId ? getAssistantSession(summaryId, signal) : Promise.resolve(null), [summaryId]);
  const [selectedMode, setSelectedMode] = useState<"build" | "ask">(() => readLocal<string>("home.mode", "build") === "ask" ? "ask" : "build");
  const mode = sessionId ? "ask" : selectedMode;
  const setMode = (next: "build" | "ask") => { writeLocal("home.mode", next); setSelectedMode(next); };
  const [chatRevision, setChatRevision] = useState(0);
  useEffect(() => {
    if (sessionId) { writeLocal("home.mode", "ask"); setSelectedMode("ask"); }
  }, [sessionId]);
  const newChat = () => {
    const scope = sessionId ? session.data?.project_id : loadHomeChatOptions().projectId;
    setObservedSessionId(null);
    removeLocal(`assistant.session.${scope ?? "home"}`);
    setAskStarted(false);
    setAskTitle(null);
    setMode("ask");
    setChatRevision((n) => n + 1);
    navigate("/");
  };
  const [validation, setValidation] = useState<string | null>(null);
  // Ask mode: set once the first question was sent; Home then shows only the conversation.
  const [askStarted, setAskStarted] = useState(false);
  // The generated title of the Ask conversation replaces the header's "Home" once the first answer arrived.
  const [askTitle, setAskTitle] = useState<string | null>(null);
  const canonicalTitle = session.data?.id === summaryId ? conversationTitleFrom(session.data.first_message ?? "") ?? t("home.askFallbackTitle") : askTitle;
  useSetPageTitle(mode === "ask" && (askStarted || sessionId !== undefined) ? canonicalTitle : null);
  const view = homeViewOf(mode, askStarted, sessionId);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  // D36.4: images and other files of the brief, uploaded in Home's scope as soon as they are added.
  const attachments = useComposerAttachments(t, null);

  const projects = useAsync((signal) => listProjects({ limit: 50 }, signal), []);
  const refreshProjects = useDebouncedCallback(projects.reload, 1500);
  useRealtimeEvents((e) => {
    if (e.type.startsWith("project.") || e.type === "task.transitioned" || e.type === "task.completed") refreshProjects();
  });

  // Persist the draft (text, destination and keys only).
  useEffect(() => {
    writeLocal(DRAFT_KEY, draft);
  }, [draft]);

  const list = projects.data?.items ?? [];
  const destinationProject = draft.destination !== "new" ? list.find((p) => p.id === draft.destination) : undefined;
  const suggestedName = deriveName(draft.text, file?.title ?? null);
  const projectName = draft.projectName || suggestedName;
  const busy = stage === "reading" || stage === "uploading" || stage === "extracting";

  const update = (patch: Partial<HomeDraft>) => setDraft((d) => ({ ...d, ...patch }));

  const onPickFile = async (f: File | undefined) => {
    if (!f) return;
    setError(null);
    setStage("reading");
    setFraction(0);
    try {
      const read = await readPrdFile(f, setFraction);
      setFile(read);
      setStage("idle");
      // New content → new import key (the same content keeps its key on retry).
      update({ prdKey: newId() });
    } catch (e) {
      setStage("error");
      const reason = e instanceof PrdFileError ? e.reason : "read_failed";
      setError({
        message:
          reason === "unsupported"
            ? t("prdImport.unsupported")
            : reason === "too_large"
              ? t("prdImport.tooLarge")
              : reason === "empty"
                ? t("prdImport.empty")
                : t("prdImport.readFailed"),
        detail: null,
        retryable: false,
        correlation: null,
      });
    } finally {
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  /**
   * D36.4: the first PRD document (Markdown, TXT, PDF, DOCX) of a drop, paste or pick becomes the PRD when none is
   * attached yet; every other file (images, screenshots, a second document…) is a composer attachment for the planner.
   */
  const onAddFiles = (files: readonly File[]) => {
    const rest: File[] = [];
    let prd: File | undefined;
    for (const f of files) {
      if (!prd && file === null && kindOf(f) !== null) prd = f;
      else rest.push(f);
    }
    if (prd) void onPickFile(prd);
    if (rest.length > 0) attachments.add(rest);
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (busy || !mayCreateProjectFromHome(mode)) return;
    if (attachments.uploading) {
      setValidation(t("composer.waitUploads"));
      return;
    }
    const text = draft.text.trim();
    if (!text && !file) {
      setValidation(t("home.validationEmpty"));
      textareaRef.current?.focus();
      return;
    }
    // A chat-style message is not a project brief: never create (and open) a project named after a greeting.
    if (draft.destination === "new" && !draft.createdProjectId && isTrivialProjectBrief(text, file !== null || attachments.ids.length > 0)) {
      setValidation(t("home.validationChatLike"));
      textareaRef.current?.focus();
      return;
    }
    if (draft.destination === "new" && !projectName.trim()) {
      setValidation(t("home.validationName"));
      return;
    }
    if (draft.destination !== "new" && !destinationProject && !projects.loading) {
      setValidation(t("home.validationDestination"));
      return;
    }
    setValidation(null);
    setError(null);
    setResult(null);
    setStage("uploading");
    setFraction(0);
    try {
      let projectId = draft.destination !== "new" ? draft.destination : draft.createdProjectId;
      let name = destinationProject?.name ?? projectName.trim();
      if (!projectId) {
        const project = await createProject(
          {
            name: projectName.trim().slice(0, 120),
            description: summarizeForDescription(text),
            autonomy_preset: draft.autonomy,
          },
          draft.projectKey,
        );
        projectId = project.id;
        name = project.name;
        update({ createdProjectId: project.id });
      }
      const prd = await importPrd(
        projectId,
        {
          title: (file?.title ?? projectName.trim() ?? name).slice(0, 300) || name,
          source_kind: file?.source_kind ?? "text",
          content: file?.content ?? text,
          content_encoding: file?.content_encoding ?? "utf8",
          start_execution: false,
          intentional_new_version: false,
        },
        draft.prdKey,
        (f) => {
          setFraction(f);
          if (f >= 1) setStage("extracting");
        },
      );
      // D36.4: the brief's attachments go to the planner with this PRD version (a planning run of the project).
      const attachmentIds = attachments.ids;
      if (attachmentIds.length > 0) await startPlanningRun(projectId, { prd_version_id: prd.prd_version_id, attachment_ids: attachmentIds });
      setResult({ project: { id: projectId, name }, prd, attachments: attachmentIds.length });
      setStage("done");
      setFile(null);
      attachments.clear();
      setDraft((d) => freshDraft(d));
      removeLocal(DRAFT_KEY);
      projects.reload();
    } catch (err) {
      setStage("error");
      const view = apiErrorView(err, t);
      setError({
        message: view.message,
        detail: view.detail,
        retryable: isApiError(err) ? err.retryable : true,
        correlation: isApiError(err) ? err.correlationId : null,
      });
    }
  };

  if (sessionId && (session.loading || session.data?.id !== sessionId)) {
    return <div className="page">{session.error ? <ErrorBanner error={session.error} onRetry={session.reload} /> : <Skeleton height={160} />}</div>;
  }

  return (
    <div className="page home" data-view={view}>
      <section className="home-hero" aria-labelledby={view === "initial" ? "home-title" : undefined} aria-label={view === "conversation" ? t("home.mode.ask") : undefined}>
        {view === "initial" ? (
        <>
        <label className="autonomy-chip">
          <GaugeIcon size={16} />
          <span className="sr-only">{t("home.autonomyLabel")}</span>
          <select
            value={draft.autonomy}
            onChange={(e) => update({ autonomy: e.target.value as AutonomyPreset })}
            disabled={draft.destination !== "new" || busy || mode === "ask"}
            aria-describedby="autonomy-hint"
          >
            {AUTONOMY_PRESETS.map((a) => (
              <option key={a} value={a}>
                {t(`autonomy.${a}`)}
              </option>
            ))}
          </select>
        </label>
        <p id="autonomy-hint" className="sr-only">
          {t("home.autonomyHint")}
        </p>
        <h1 id="home-title" className="home-title">
          {t(mode === "ask" ? "home.chatTitle" : "home.title")}
        </h1>
        <p className="home-subtitle">{t("home.subtitle")}</p>

        <div className="segmented home-mode" role="group" aria-label={t("home.mode")}>
          <button type="button" aria-pressed={mode === "build"} onClick={() => setMode("build")}>
            {t("home.mode.build")}
          </button>
          <button type="button" aria-pressed={mode === "ask"} onClick={() => setMode("ask")}>
            {t("home.mode.ask")}
          </button>
        </div>
        </>
        ) : null}

        {mode === "ask" ? <>
          <div className="row"><Button size="sm" onClick={newChat}>{t("home.newChat")}</Button></div>
          <HomeAsk key={`${sessionId ?? "current"}:${chatRevision}`} {...(sessionId ? { sessionId, sessionProjectId: session.data?.project_id ?? null } : {})} projects={list} projectsLoaded={!projects.loading && !projects.error} onNewProject={() => setDialog("project")} onConversationActive={setAskStarted} onTitle={setAskTitle} onSessionChanged={(id) => { setObservedSessionId(id); if (!id && sessionId) newChat(); }} />
        </> : null}

        {mode === "build" ? (
        <>
        <form className="composer" onSubmit={submit} aria-describedby="composer-destination">
          {/* D36: the shared composer (Enter sends, Shift+Enter adds a line, paste or drop the PRD file, dictation). */}
          <Composer
            id="home-composer"
            inputRef={textareaRef}
            label={t("home.inputLabel")}
            placeholder={t("home.placeholder")}
            hint={t("assistant.composer.hint")}
            value={draft.text}
            onChange={(text) => {
              // Changing the text after a failure is a new request: new PRD key.
              update({ text, ...(stage === "error" ? { prdKey: newId() } : {}) });
              if (stage === "error") setStage("idle");
            }}
            onSubmit={() => void submit()}
            maxChars={HOME_BRIEF_MAX}
            disabled={busy}
            invalid={validation !== null}
            rows={3}
            className="cmp--home"
            inputClassName="composer-input"
            attachments={[
              ...(file
                ? [{ key: "prd", name: file.name, size: file.size, kind: file.source_kind === "pdf" ? ("pdf" as const) : file.source_kind === "docx" ? ("document" as const) : ("text" as const) }]
                : []),
              ...attachments.items,
            ]}
            onAddFiles={onAddFiles}
            onRemoveAttachment={(key) => (key === "prd" ? setFile(null) : attachments.remove(key))}
            accept={HOME_ACCEPT}
            multiple
            attachLabel={t("home.attachFiles")}
            attachHint={t("home.attachFilesHint")}
            fileInputTestId="home-composer-file-input"
            dictation
            controls={
              <>
                <span className="composer-divider" aria-hidden="true" />
                <label className="destination-select">
                  <FileTextIcon size={16} />
                  <span className="sr-only">{t("home.destination")}</span>
                  <select value={draft.destination} onChange={(e) => update({ destination: e.target.value, createdProjectId: null, projectKey: newId(), prdKey: newId() })} disabled={busy}>
                    <option value="new">{t("home.newProject")}</option>
                    {list.length > 0 ? (
                      <optgroup label={t("home.existingProjects")}>
                        {list.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </optgroup>
                    ) : null}
                  </select>
                </label>
              </>
            }
            actions={
              <IconButton type="submit" variant="primary" label={t("home.send")} disabled={busy} aria-busy={busy || undefined}>
                {busy ? <span className="spinner" aria-hidden="true" /> : <SendIcon size={18} />}
              </IconButton>
            }
          />
        </form>
        <input
          ref={fileInput}
          type="file"
          accept={HOME_ACCEPT}
          multiple
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          data-testid="home-file-input"
          onChange={(e) => {
            onAddFiles([...(e.target.files ?? [])]);
            e.target.value = "";
          }}
        />

        <div id="composer-destination" className="destination-summary">
          {draft.destination === "new" ? (
            <div className="destination-new">
              <span className="muted">{t("home.destinationNew")}</span>
              <label className="destination-name">
                <span className="sr-only">{t("project.name")}</span>
                <input
                  className="input input--inline"
                  value={draft.projectName}
                  placeholder={suggestedName || t("home.projectNamePlaceholder")}
                  onChange={(e) => update({ projectName: e.target.value })}
                  maxLength={120}
                  disabled={busy}
                  aria-label={t("project.name")}
                />
              </label>
              <span className="subtle">· {t("home.destinationAutonomy", { preset: t(`autonomy.${draft.autonomy}`) })}</span>
            </div>
          ) : (
            <p className="muted">{t("home.destinationExisting", { name: destinationProject?.name ?? "…" })}</p>
          )}
        </div>

        <div className="home-feedback" aria-live="polite">
          {validation ? (
            <p className="field-error" role="alert">
              {validation}
            </p>
          ) : null}
          {attachments.rejections.length > 0 ? (
            <Banner tone="warning" role="alert">
              {attachments.rejections.map((r) => (
                <p key={r}>{r}</p>
              ))}
            </Banner>
          ) : null}
          <ImportProgress stage={stage} fraction={fraction} />
          {stage === "error" && error ? (
            <Banner
              tone="error"
              role="alert"
              title={t("home.submitFailed")}
              meta={error.correlation ? t("error.correlation", { id: error.correlation }) : undefined}
              action={
                error.retryable ? (
                  <Button size="sm" variant="primary" onClick={() => void submit()} data-testid="home-retry">
                    {t("common.retry")}
                  </Button>
                ) : undefined
              }
            >
              <p>{error.message}</p>
              {error.detail && error.detail !== error.message ? (
                <p className="field-hint user-content" data-testid="home-error-detail">
                  {t("apiError.detail", { detail: error.detail })}
                </p>
              ) : null}
              <p className="field-hint">{t("home.draftKept")}</p>
            </Banner>
          ) : null}
          {stage === "done" && result ? (
            <div className="card card--pad home-result" data-testid="home-result">
              <p className="banner-title">{t("home.created", { name: result.project.name })}</p>
              <ImportResultSummary result={result.prd} />
              {result.attachments > 0 ? <p className="muted">{t("home.planningWithAttachments", { n: result.attachments })}</p> : null}
              <div className="row row--wrap">
                <Button variant="primary" iconEnd={<ArrowRightIcon size={16} />} onClick={() => navigate(`/projects/${result.project.id}`)}>
                  {t("home.openProject")}
                </Button>
                <Button onClick={() => navigate(`/projects/${result.project.id}/kanban`)}>{t("home.openKanban")}</Button>
                <Button variant="ghost" onClick={() => navigate(`/projects/${result.project.id}/prds`)}>
                  {t("home.reviewPending")}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
        </>
        ) : null}

        {view === "initial" ? (
        <ul className="suggestions" aria-label={t("home.suggestions")}>
          <li>
            <button type="button" className="suggestion" onClick={() => setDialog("project")}>
              <PlusIcon />
              {t("home.createApp")}
            </button>
          </li>
          <li>
            <button type="button" className="suggestion" onClick={() => setDialog("import")}>
              <GitBranchIcon />
              {t("home.importRepo")}
            </button>
          </li>
          <li>
            <button type="button" className="suggestion" onClick={() => setDialog("fix")}>
              <WrenchIcon />
              {t("home.fixProblem")}
            </button>
          </li>
        </ul>
        ) : null}
      </section>

      {view === "initial" ? (
      <>
      <ConversationHistory />
      <section className="home-recent" aria-labelledby="recent-title">
        <div className="section-head">
          <h2 id="recent-title">{t("nav.projects")}</h2>
          {list.length > 0 ? (
            <Link to="/projects" className="link-subtle">
              {t("home.viewAll")} <ArrowRightIcon size={16} />
            </Link>
          ) : null}
        </div>
        {projects.loading ? (
          <div className="stack" role="status" aria-label={t("common.loading")}>
            <Skeleton height={76} />
            <Skeleton height={76} />
          </div>
        ) : projects.error ? (
          <ErrorBanner error={projects.error} onRetry={projects.reload} />
        ) : list.length === 0 ? (
          <div className="card">
            <EmptyState
              icon={<FolderIcon />}
              title={t("home.emptyTitle")}
              actions={
                <>
                  <Button variant="primary" icon={<PlusIcon size={16} />} onClick={() => setDialog("project")}>
                    {t("projects.new")}
                  </Button>
                  <Button icon={<PaperclipIcon size={16} />} onClick={() => fileInput.current?.click()}>
                    {t("home.attachPrd")}
                  </Button>
                  <Button variant="ghost" icon={<ServerIcon size={16} />} onClick={() => navigate("/settings")}>
                    {t("home.configureInfra")}
                  </Button>
                </>
              }
            >
              <p>{t("home.emptyBody")}</p>
            </EmptyState>
          </div>
        ) : (
          <ul className="recent-list">
            {list.slice(0, 5).map((p) => (
              <RecentRow key={p.id} project={p} />
            ))}
          </ul>
        )}
      </section>
      </>
      ) : null}

      <CreateProjectDialog open={dialog === "project" || dialog === "import"} mode={dialog === "import" ? "import" : "new"} onClose={() => setDialog(null)} />
      <CreateTaskDialog open={dialog === "fix"} defaultType="bugfix" onClose={() => setDialog(null)} />
    </div>
  );
}

/** Short project description from the first meaningful line of the request (the full text is imported as the PRD). */
export function summarizeForDescription(text: string): string {
  const line = text
    .split("\n")
    .map((l) => l.replace(/^\s*(#{1,6}\s+|[-*+]\s+|\d+[.)]\s+)/, "").trim())
    .find((l) => l.length > 0) ?? "";
  return line.length > 200 ? `${line.slice(0, 199)}…` : line;
}
